// --- Dependencies -------------------------------------------------------------------------------
import { useRef } from "react";
import { useLocation } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useAuth from "@/hooks/useAuth";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import ProfilePopup from "@/UI/popups/ProfilePopup";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    onMenuClick: () => void;
};

// --- Logic --------------------------------------------------------------------------------------

export default function AppBar({ onMenuClick }: Props) {
    const t = useTranslation();
    const admin = useAuth((state) => state.admin);
    const { addComp, removeComp } = usePopup();
    const { pathname } = useLocation();
    const profileRef = useRef<HTMLButtonElement>(null);

    const ROUTE_LABELS: Record<string, string> = {
        "/": t.sideBar.home,
        "/stores": t.sideBar.stores,
        "/clients": t.sideBar.clients,
        "/coupons": t.sideBar.coupons,
        "/subscriptions": t.sideBar.subscriptions,
        "/support": t.sideBar.support,
        "/logs": t.sideBar.logs,
    };

    const pageTitle =
        Object.entries(ROUTE_LABELS)
            .filter(([key]) => pathname === key || (key !== "/" && pathname.startsWith(key + "/")))
            .sort((a, b) => b[0].length - a[0].length)[0]?.[1] ?? "";

    const openProfileMenu = () => {
        const rect = profileRef.current?.getBoundingClientRect();
        if (!rect) return;
        const name = "profile-menu";
        removeComp(name);
        addComp({
            name,
            Component: ProfilePopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            coverClose: true,
            anchor: { top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom, width: rect.width, height: rect.height },
        });
    };

    return (
        <header className="app-bar">
            <button type="button" className="app-bar-menu-btn" aria-label="Menu" onClick={onMenuClick}>
                <img src="/icons/menu-icon.svg" alt="" width={18} height={16} />
            </button>
            <p className="app-bar-title">{pageTitle}</p>
            <div className="app-bar-spacer" />
            <div className="app-bar-actions">
                {admin && (
                    <button ref={profileRef} type="button" className="app-bar-profile" onClick={openProfileMenu}>
                        <div className="app-bar-profile-avatar">
                            {admin.picture ? (
                                <img src={admin.picture} alt={admin.name} />
                            ) : (
                                <img
                                    className="app-bar-profile-avatar-placeholder"
                                    src="/icons/client-small-placeholder-icon.svg"
                                    alt={admin.name}
                                    width={20}
                                    height={20}
                                />
                            )}
                        </div>
                        <p className="app-bar-profile-name">{admin.name}</p>
                    </button>
                )}
            </div>
        </header>
    );
}
